import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Users, Clock, User, ShoppingCart, Timer, ChefHat, Plus, Eye, CreditCard, Bell, X } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * Modern Table Card Component
 * 
 * A visually modern card for displaying table information
 * with status colors, animations, and real-time data
 * 
 * Visibility rules:
 * - ADMIN: Can see order total on ALL tables
 * - Other roles: Can only see order total on THEIR OWN tables (where they are the assigned server)
 */

// Status configuration with colors and labels
const STATUS_CONFIG = {
    // Libre (Green)
    libre: {
        label: 'Libre',
        bgColor: 'bg-emerald-50',
        borderColor: 'border-emerald-400',
        textColor: 'text-emerald-700',
        badgeBg: 'bg-emerald-100',
        badgeText: 'text-emerald-800',
        badgeBorder: 'border-emerald-300',
        hoverBorder: 'hover:border-emerald-500',
        selectedRing: 'ring-emerald-400'
    },
    free: {
        label: 'Libre',
        bgColor: 'bg-emerald-50',
        borderColor: 'border-emerald-400',
        textColor: 'text-emerald-700',
        badgeBg: 'bg-emerald-100',
        badgeText: 'text-emerald-800',
        badgeBorder: 'border-emerald-300',
        hoverBorder: 'hover:border-emerald-500',
        selectedRing: 'ring-emerald-400'
    },
    available: {
        label: 'Libre',
        bgColor: 'bg-emerald-50',
        borderColor: 'border-emerald-400',
        textColor: 'text-emerald-700',
        badgeBg: 'bg-emerald-100',
        badgeText: 'text-emerald-800',
        badgeBorder: 'border-emerald-300',
        hoverBorder: 'hover:border-emerald-500',
        selectedRing: 'ring-emerald-400'
    },
    // Occupée - En cours (Orange)
    occupee: {
        label: 'Occupée',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-400',
        textColor: 'text-orange-700',
        badgeBg: 'bg-orange-100',
        badgeText: 'text-orange-800',
        badgeBorder: 'border-orange-300',
        hoverBorder: 'hover:border-orange-500',
        selectedRing: 'ring-orange-400'
    },
    occupied: {
        label: 'Occupée',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-400',
        textColor: 'text-orange-700',
        badgeBg: 'bg-orange-100',
        badgeText: 'text-orange-800',
        badgeBorder: 'border-orange-300',
        hoverBorder: 'hover:border-orange-500',
        selectedRing: 'ring-orange-400'
    },
    // À encaisser (Rouge)
    a_encaisser: {
        label: 'À encaisser',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-400',
        textColor: 'text-red-700',
        badgeBg: 'bg-red-100',
        badgeText: 'text-red-800',
        badgeBorder: 'border-red-300',
        hoverBorder: 'hover:border-red-500',
        selectedRing: 'ring-red-400'
    },
    paid: {
        label: 'Payée',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-400',
        textColor: 'text-green-700',
        badgeBg: 'bg-green-100',
        badgeText: 'text-green-800',
        badgeBorder: 'border-green-300',
        hoverBorder: 'hover:border-green-500',
        selectedRing: 'ring-green-400'
    },
    // À débarrasser (Amber)
    a_debarrasser: {
        label: 'À débarrasser',
        bgColor: 'bg-amber-50',
        borderColor: 'border-amber-500',
        textColor: 'text-amber-700',
        badgeBg: 'bg-amber-100',
        badgeText: 'text-amber-800',
        badgeBorder: 'border-amber-300',
        hoverBorder: 'hover:border-amber-600',
        selectedRing: 'ring-amber-400'
    },
    // Réservée (Yellow)
    reservee: {
        label: 'Réservée',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-400',
        textColor: 'text-yellow-700',
        badgeBg: 'bg-yellow-100',
        badgeText: 'text-yellow-800',
        badgeBorder: 'border-yellow-300',
        hoverBorder: 'hover:border-yellow-500',
        selectedRing: 'ring-yellow-400'
    },
    reserved: {
        label: 'Réservée',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-400',
        textColor: 'text-yellow-700',
        badgeBg: 'bg-yellow-100',
        badgeText: 'text-yellow-800',
        badgeBorder: 'border-yellow-300',
        hoverBorder: 'hover:border-yellow-500',
        selectedRing: 'ring-yellow-400'
    },
    // Fermée / Indisponible (Gray)
    fermee: {
        label: 'Fermée',
        bgColor: 'bg-gray-100',
        borderColor: 'border-gray-300',
        textColor: 'text-gray-500',
        badgeBg: 'bg-gray-200',
        badgeText: 'text-gray-600',
        badgeBorder: 'border-gray-300',
        hoverBorder: 'hover:border-gray-400',
        selectedRing: 'ring-gray-300'
    },
    indisponible: {
        label: 'Indisponible',
        bgColor: 'bg-gray-100',
        borderColor: 'border-gray-300',
        textColor: 'text-gray-500',
        badgeBg: 'bg-gray-200',
        badgeText: 'text-gray-600',
        badgeBorder: 'border-gray-300',
        hoverBorder: 'hover:border-gray-400',
        selectedRing: 'ring-gray-300'
    },
    // Fusionnée (Violet)
    fusionnee: {
        label: 'Fusionnée',
        bgColor: 'bg-violet-50',
        borderColor: 'border-violet-400',
        textColor: 'text-violet-700',
        badgeBg: 'bg-violet-100',
        badgeText: 'text-violet-800',
        badgeBorder: 'border-violet-300',
        hoverBorder: 'hover:border-violet-500',
        selectedRing: 'ring-violet-400'
    }
};

// Kitchen status colors for indicator - Enhanced with icons
const KITCHEN_STATUS = {
    sent: { 
        color: 'bg-blue-500', 
        label: 'Cuisine', 
        badgeBg: 'bg-blue-100', 
        badgeText: 'text-blue-700',
        borderColor: 'border-blue-300',
        icon: 'sent'
    },
    preparing: { 
        color: 'bg-amber-500', 
        label: 'En prépa', 
        badgeBg: 'bg-amber-100', 
        badgeText: 'text-amber-700',
        borderColor: 'border-amber-300',
        icon: 'preparing'
    },
    ready: { 
        color: 'bg-red-500', 
        label: 'Prête!', 
        badgeBg: 'bg-red-100', 
        badgeText: 'text-red-700',
        borderColor: 'border-red-400',
        icon: 'ready'
    }
};

// Quick Actions configuration
const QUICK_ACTIONS = [
    { id: 'add', label: 'Ajouter', icon: Plus, color: 'bg-blue-500 hover:bg-blue-600' },
    { id: 'view', label: 'Voir', icon: Eye, color: 'bg-gray-500 hover:bg-gray-600' },
    { id: 'pay', label: 'Encaisser', icon: CreditCard, color: 'bg-green-500 hover:bg-green-600' },
    { id: 'call', label: 'Manager', icon: Bell, color: 'bg-orange-500 hover:bg-orange-600' }
];

// Default status config
const DEFAULT_STATUS = {
    label: 'Inconnu',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-300',
    textColor: 'text-gray-600',
    badgeBg: 'bg-gray-100',
    badgeText: 'text-gray-600',
    badgeBorder: 'border-gray-200',
    hoverBorder: 'hover:border-gray-400',
    selectedRing: 'ring-gray-300'
};

/**
 * Format currency to FCFA
 */
const formatCFA = (amount) => {
    if (!amount || amount === 0) return null;
    return `${amount.toLocaleString('fr-FR')} F`;
};

/**
 * Calculate elapsed time from opening (returns object for real-time updates)
 */
const calculateElapsedTime = (openedAt) => {
    if (!openedAt) return null;
    
    const opened = new Date(openedAt);
    const now = new Date();
    const diffMs = now - opened;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `${diffMins} min`;
    
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hours}h${mins > 0 ? String(mins).padStart(2, '0') : ''}`;
};

/**
 * ModernTableCard Component
 * 
 * @param {Object} table - Table data
 * @param {Function} onClick - Click handler
 * @param {Function} onQuickAction - Quick action handler (action, table) => void
 * @param {boolean} isSelected - Is this table selected
 * @param {boolean} isTransferSource - Is this table the source for transfer
 * @param {boolean} isMergeSelected - Is this table selected for merge
 * @param {boolean} showDetails - Show detailed info (places, server, etc.)
 * @param {boolean} showQuickActions - Enable quick actions menu for occupied tables
 * @param {string} size - Card size: 'compact', 'normal', 'large'
 * @param {Object} currentUser - Current logged in user { id, role, name }
 * @param {string} className - Additional CSS classes
 */
const ModernTableCard = ({
    table,
    onClick,
    onQuickAction,
    isSelected = false,
    isTransferSource = false,
    isMergeSelected = false,
    showDetails = true,
    showQuickActions = true,
    size = 'normal',
    currentUser = null,
    className = ''
}) => {
    // State for real-time elapsed time
    const [elapsedTime, setElapsedTime] = useState(null);
    // State for quick actions menu
    const [showActionsMenu, setShowActionsMenu] = useState(false);
    // State for status transition animation
    const [prevStatus, setPrevStatus] = useState(table?.status);
    const [isStatusTransitioning, setIsStatusTransitioning] = useState(false);
    const menuRef = useRef(null);
    const cardRef = useRef(null);
    
    // Get status configuration
    const statusConfig = useMemo(() => {
        const status = table?.status?.toLowerCase() || 'libre';
        return STATUS_CONFIG[status] || DEFAULT_STATUS;
    }, [table?.status]);
    
    // Determine if table is occupied
    const isOccupied = useMemo(() => {
        return ['occupee', 'occupied', 'a_encaisser', 'a_debarrasser', 'paid'].includes(table?.status?.toLowerCase());
    }, [table?.status]);
    
    // Detect status change and trigger animation
    useEffect(() => {
        if (prevStatus !== table?.status && table?.status) {
            setIsStatusTransitioning(true);
            const timer = setTimeout(() => {
                setIsStatusTransitioning(false);
                setPrevStatus(table?.status);
            }, 600);
            return () => clearTimeout(timer);
        }
    }, [table?.status, prevStatus]);
    
    // Real-time elapsed time update (every minute)
    useEffect(() => {
        const openedAt = table?.opened_at || table?.current_order?.created_at;
        
        if (!openedAt || !isOccupied) {
            setElapsedTime(null);
            return;
        }
        
        // Calculate immediately
        setElapsedTime(calculateElapsedTime(openedAt));
        
        // Update every minute
        const interval = setInterval(() => {
            setElapsedTime(calculateElapsedTime(openedAt));
        }, 60000); // 60 seconds
        
        return () => clearInterval(interval);
    }, [table?.opened_at, table?.current_order?.created_at, isOccupied]);
    
    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target) &&
                cardRef.current && !cardRef.current.contains(event.target)) {
                setShowActionsMenu(false);
            }
        };
        
        if (showActionsMenu) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showActionsMenu]);
    
    // Handle card click - show quick actions for occupied tables
    const handleCardClick = (e) => {
        e.stopPropagation();
        
        if (isOccupied && showQuickActions && onQuickAction) {
            setShowActionsMenu(prev => !prev);
        } else {
            onClick?.(table);
        }
    };
    
    // Handle quick action selection
    const handleQuickAction = (action, e) => {
        e.stopPropagation();
        setShowActionsMenu(false);
        onQuickAction?.(action, table);
    };
    
    // Determine if current user can see order total
    // Tout le monde peut voir le total des tables occupées
    const canSeeOrderTotal = useMemo(() => {
        // Seulement pour les tables occupées avec des items
        if (!isOccupied) return false;
        
        const hasItems = (table?.sent_items?.length > 0) || (table?.draft_items?.length > 0);
        const hasTotal = (table?.current_order?.total > 0) || (table?.order_total > 0);
        
        return hasItems || hasTotal;
    }, [isOccupied, table]);
    
    // Get order total (only if table is occupied and has items)
    const orderTotal = useMemo(() => {
        if (!canSeeOrderTotal) return null;
        
        // Calculer depuis les items
        const itemsTotal = 
            (table?.sent_items?.reduce((sum, item) => sum + (item.line_total || 0), 0) || 0) +
            (table?.draft_items?.reduce((sum, item) => sum + (item.line_total || 0), 0) || 0);
        
        const total = itemsTotal || table?.current_order?.total || table?.order_total || 0;
        return total > 0 ? formatCFA(total) : null;
    }, [table, canSeeOrderTotal]);
    
    // Determine kitchen status
    const kitchenStatus = useMemo(() => {
        if (!table?.current_order) return null;
        const order = table.current_order;
        if (order.kitchen_status === 'ready' || order.ready_items > 0) return 'ready';
        if (order.kitchen_status === 'preparing' || order.preparing_items > 0) return 'preparing';
        if (order.sent_to_kitchen || order.items?.length > 0) return 'sent';
        return null;
    }, [table?.current_order]);
    
    // Get server name
    const serverName = table?.server_name || table?.waiter_name || table?.assigned_server;
    
    // Size classes
    const sizeClasses = {
        compact: 'min-w-[120px] min-h-[95px]',
        normal: 'min-w-[150px] min-h-[115px]',
        large: 'min-w-[180px] min-h-[135px]'
    };
    
    const numberSizeClasses = {
        compact: 'text-2xl',
        normal: 'text-3xl',
        large: 'text-4xl'
    };
    
    return (
        <div
            ref={cardRef}
            data-testid={`table-card-${table?.number || table?.id}`}
            onClick={handleCardClick}
            className={cn(
                // Base styles
                "relative cursor-pointer rounded-[14px] border-2 p-3 sm:p-4",
                "transition-all duration-300 ease-out",
                "shadow-sm hover:shadow-lg",
                
                // Size
                sizeClasses[size] || sizeClasses.normal,
                
                // Status colors (10% background, colored border)
                statusConfig.bgColor,
                statusConfig.borderColor,
                statusConfig.hoverBorder,
                
                // Hover animation - scale
                "hover:scale-[1.03] active:scale-[0.98]",
                
                // Selection states
                isSelected && `ring-4 ${statusConfig.selectedRing} ring-offset-2 border-4 shadow-xl`,
                isTransferSource && "ring-4 ring-blue-500 ring-offset-2 animate-pulse",
                isMergeSelected && "ring-4 ring-purple-500 ring-offset-2",
                
                // Status transition animation
                isStatusTransitioning && "scale-105 shadow-xl",
                
                // Custom classes
                className
            )}
            style={{
                // Smooth color transition
                transition: 'all 0.3s ease-out, background-color 0.4s ease-in-out, border-color 0.4s ease-in-out'
            }}
        >
            {/* Status change ripple effect */}
            {isStatusTransitioning && (
                <div className="absolute inset-0 rounded-[12px] overflow-hidden pointer-events-none z-0">
                    <div 
                        className="absolute inset-0 bg-white/40" 
                        style={{
                            animation: 'pulse 0.6s ease-out'
                        }}
                    />
                </div>
            )}
            
            {/* Kitchen Status Badge - Top Left - Enhanced with icon */}
            {kitchenStatus && (
                <div className="absolute top-1.5 left-1.5 z-10">
                    <span className={cn(
                        "flex items-center gap-1 text-[8px] sm:text-[9px] font-bold px-1.5 py-0.5 rounded-full border shadow-sm",
                        "transition-all duration-300",
                        KITCHEN_STATUS[kitchenStatus]?.badgeBg,
                        KITCHEN_STATUS[kitchenStatus]?.badgeText,
                        KITCHEN_STATUS[kitchenStatus]?.borderColor,
                        kitchenStatus === 'ready' && "animate-pulse shadow-red-200"
                    )}>
                        <ChefHat className="w-2.5 h-2.5" />
                        {KITCHEN_STATUS[kitchenStatus]?.label}
                    </span>
                </div>
            )}
            
            {/* Quick Actions Menu - Floating above the card */}
            {showActionsMenu && isOccupied && (
                <div 
                    ref={menuRef}
                    className="absolute -top-14 left-1/2 transform -translate-x-1/2 z-50 animate-in fade-in slide-in-from-bottom-2 duration-150"
                    data-testid={`quick-actions-menu-${table?.number}`}
                >
                    <div className="bg-white rounded-xl shadow-xl border border-gray-200 p-1.5 flex items-center gap-1">
                        {QUICK_ACTIONS.map((action) => {
                            const Icon = action.icon;
                            return (
                                <button
                                    key={action.id}
                                    onClick={(e) => handleQuickAction(action.id, e)}
                                    className={cn(
                                        "flex flex-col items-center justify-center w-12 h-12 rounded-lg text-white transition-all",
                                        "hover:scale-105 active:scale-95",
                                        action.color
                                    )}
                                    data-testid={`quick-action-${action.id}-${table?.number}`}
                                    title={action.label}
                                >
                                    <Icon className="w-4 h-4" />
                                    <span className="text-[8px] mt-0.5 font-medium">{action.label}</span>
                                </button>
                            );
                        })}
                        <button
                            onClick={(e) => { e.stopPropagation(); setShowActionsMenu(false); }}
                            className="flex items-center justify-center w-8 h-12 rounded-lg text-gray-500 hover:bg-gray-100 transition-all"
                            title="Fermer"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                    {/* Arrow pointer */}
                    <div className="absolute -bottom-1.5 left-1/2 transform -translate-x-1/2 w-3 h-3 bg-white border-r border-b border-gray-200 rotate-45" />
                </div>
            )}
            
            {/* Main Content */}
            <div className="flex flex-col h-full">
                {/* Table Number - Most Prominent */}
                <div className="text-center mb-1">
                    <span className={cn(
                        "font-black tracking-tight leading-none",
                        numberSizeClasses[size] || numberSizeClasses.normal,
                        "text-gray-800"
                    )}>
                        {table?.number || table?.name}
                    </span>
                </div>
                
                {/* Table Details */}
                {showDetails && (
                    <div className="space-y-0.5 flex-1 text-center">
                        {/* Capacity */}
                        <div className="flex items-center justify-center gap-1 text-[11px] text-gray-600">
                            <Users className="w-3 h-3" />
                            <span>{table?.capacity || 4} places</span>
                        </div>
                        
                        {/* Server Name (if assigned) - always visible when table is occupied */}
                        {isOccupied && serverName && (
                            <div className="flex items-center justify-center gap-1 text-[10px] text-gray-600 font-medium truncate px-1">
                                <User className="w-3 h-3 flex-shrink-0" />
                                <span className="truncate max-w-[90px]">{serverName}</span>
                            </div>
                        )}
                        
                        {/* Order Total (if occupied AND user can see it) */}
                        {orderTotal && isOccupied && (
                            <div className="flex items-center justify-center gap-1 text-[11px] font-bold text-green-700">
                                <ShoppingCart className="w-3 h-3 flex-shrink-0" />
                                <span>{orderTotal}</span>
                            </div>
                        )}
                        
                        {/* Elapsed Time (if occupied) - Real-time updates */}
                        {elapsedTime && isOccupied && (
                            <div className="flex items-center justify-center gap-1 text-[10px] text-gray-500">
                                <Timer className="w-2.5 h-2.5 flex-shrink-0" />
                                <span>{elapsedTime}</span>
                            </div>
                        )}
                    </div>
                )}
                
                {/* Status Badge - Bottom (more visible than just a dot) */}
                <div className="mt-auto pt-1.5">
                    <div className="flex justify-center">
                        <span className={cn(
                            "text-[9px] sm:text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border",
                            statusConfig.badgeBg,
                            statusConfig.badgeText,
                            statusConfig.badgeBorder
                        )}>
                            {statusConfig.label}
                        </span>
                    </div>
                </div>
            </div>
            
            {/* Ready to Serve Alert Badge */}
            {kitchenStatus === 'ready' && (
                <div className="absolute -top-1 -right-1 flex items-center justify-center">
                    <span className="relative flex h-4 w-4">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-4 w-4 bg-red-500 items-center justify-center text-white text-[8px] font-bold">
                            !
                        </span>
                    </span>
                </div>
            )}
        </div>
    );
};

/**
 * Compact version for POS and smaller spaces
 */
export const CompactTableCard = (props) => (
    <ModernTableCard {...props} size="compact" />
);

/**
 * Large version for tablet/touch interfaces
 */
export const LargeTableCard = (props) => (
    <ModernTableCard {...props} size="large" />
);

export default ModernTableCard;
